/* Quick add helpers shared by the card bottom sheet (sg-quick-add-sheet.js) and Complete the look on product pages
   (sg-complete-look.js). On top of the theme's quick view they add a header (photo, title, price, link), and mirror the
   option dropdowns as swatches / option buttons that drive the hidden dropdowns, so the theme's variant logic, prices
   and Add to cart keep working unchanged. Styles: .sg-qa-sheet__* in design-system.css. */
(() => {
  if (window.sgQaOptions) return;

  const colorPattern = /colou?r|metal|finish|tone/i;
  const caseValue = (value) => (typeof window.sgOptionCase === 'function' ? window.sgOptionCase(value) : value);

  // link: the product link (its text is the title), image: an <img>, price: the price element as the page shows it
  function header (popup, { link, image, price }) {
    const content = popup.querySelector('.advanced-modal__content');
    if (!content) return;
    content.querySelector('.sg-qa-sheet__header')?.remove();

    const element = document.createElement('div');
    element.className = 'sg-qa-sheet__header';
    element.innerHTML = `
      <div class="sg-qa-sheet__thumb">${image ? `<img src="${image.currentSrc || image.src}" alt="" width="72" height="72">` : ''}</div>
      <div class="sg-qa-sheet__info">
        <p class="sg-qa-sheet__title"></p>
        <p class="sg-qa-sheet__price"></p>
        ${link ? `<a class="sg-qa-sheet__link" href="${link.getAttribute('href')}">View details</a>` : ''}
      </div>`;
    element.querySelector('.sg-qa-sheet__title').textContent = (link?.textContent || '').trim();
    element.querySelector('.sg-qa-sheet__price').append(...priceParts(price));
    content.prepend(element);
  }

  // The price as the page shows it: the current price, plus the crossed-out compare-at price on a sale. The theme's
  // price snippet prints both the regular and the sale markup and hides one with CSS, so it is read, not copied.
  function priceParts (price) {
    if (!price) return [];
    const text = (node) => (node?.textContent || '').replace(/\s+/g, ' ').trim();
    const block = price.matches('.price') ? price : price.querySelector('.price');
    let current = '';
    let compare = '';
    if (block) {
      const onSale = block.classList.contains('price--on-sale');
      current = text(block.querySelector(onSale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular'));
      if (onSale) compare = text(block.querySelector('.price__sale s, .price__sale .price-item--regular'));
    }
    if (!current) {
      // e.g. "$24 Single | $38 Pair": the visible text without the screen-reader-only "Regular price" labels
      current = price.innerText || '';
      price.querySelectorAll('.visually-hidden').forEach((item) => {
        const label = item.textContent.trim();
        if (label) current = current.replace(label, '');
      });
      current = current.replace(/\s+/g, ' ').trim();
    }
    const parts = [];
    const main = document.createElement('span');
    main.textContent = current;
    parts.push(main);
    if (compare) {
      const struck = document.createElement('s');
      struck.textContent = compare;
      parts.push(' ', struck);
    }
    return parts;
  }

  function build (popup) {
    popup.querySelectorAll('.variant-wrapper').forEach((wrapper) => {
      if (wrapper.hidden || wrapper.classList.contains('sg-qa-enhanced')) return;
      const select = wrapper.querySelector('select');
      if (!select) return;

      const name = (wrapper.querySelector('.variant__label')?.firstChild?.textContent || select.closest('[data-handle]')?.dataset.handle || '')
        .replace(':', '')
        .trim();
      const isColor = colorPattern.test(name);

      const label = document.createElement('p');
      label.className = 'sg-qa-sheet__label';
      label.innerHTML = '<strong></strong> <span></span>';
      label.querySelector('strong').textContent = `${name}:`;

      const group = document.createElement('div');
      group.className = `sg-qa-sheet__options${isColor ? ' sg-qa-sheet__options--swatches' : ''}`;
      group.setAttribute('role', 'radiogroup');
      group.setAttribute('aria-label', name);

      Array.from(select.options).forEach((option) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('role', 'radio');
        button.dataset.value = option.value;
        if (isColor) {
          button.className = 'sg-qa-sheet__swatch';
          button.dataset.color = option.value;
          button.setAttribute('aria-label', caseValue(option.value));
          button.innerHTML = '<span></span>';
        } else {
          button.className = 'sg-qa-sheet__option';
          button.textContent = caseValue(option.value);
        }
        button.addEventListener('click', () => {
          if (button.disabled || select.value === option.value) return;
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          requestAnimationFrame(() => sync(popup));
        });
        group.appendChild(button);
      });

      wrapper.classList.add('sg-qa-enhanced');
      wrapper.prepend(label, group);
      select.addEventListener('change', () => requestAnimationFrame(() => sync(popup)));
    });
    sync(popup);
  }

  // Mirrors the dropdowns' selected / disabled state, the "Color: Gold" value and the variant photo.
  function sync (popup) {
    popup.querySelectorAll('.variant-wrapper.sg-qa-enhanced').forEach((wrapper) => {
      const select = wrapper.querySelector('select');
      const value = wrapper.querySelector('.sg-qa-sheet__label span');
      if (value) value.textContent = caseValue(select.value);
      wrapper.querySelectorAll('.sg-qa-sheet__options button').forEach((button) => {
        const option = Array.from(select.options).find((item) => item.value === button.dataset.value);
        const selected = select.value === button.dataset.value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', String(selected));
        button.disabled = Boolean(option?.disabled);
      });
    });

    const variantId = popup.querySelector('select[name="id"]')?.value;
    const json = popup.querySelector('script[id^="VariantsJson-"]');
    const thumb = popup.querySelector('.sg-qa-sheet__thumb img');
    if (!variantId || !json || !thumb) return;
    try {
      const variant = JSON.parse(json.textContent).find((item) => String(item.id) === String(variantId));
      const src = variant?.featured_image?.src;
      // only when it is another photo: re-setting the same file with a new size reloaded it (a blank flash on open)
      const file = (url) => new URL(url, location.href).pathname;
      if (src && file(src) !== file(thumb.currentSrc || thumb.src)) thumb.src = `${src}${src.includes('?') ? '&' : '?'}width=200`;
    } catch (error) {
      /* keep the page photo */
    }
  }

  // Photo strip across the top of the quick add (Van 2026-10-22, after Pavoi; phone card sheet + Complete the look): the card's own photos straight away, then all
  // the product's photos once /products/<handle>.js answers (cached per product). Swipe sideways; the small thumb
  // in the header hides while the strip is there.
  const photoCache = new Map();
  const photoUrl = (src, width) => {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;
    return url + (url.includes('?') ? '&' : '?') + `width=${width}`;
  };
  // Same file whatever the size/version query
  // The storefront serves a photo from /cdn/shop/files/… and product JSON from cdn.shopify.com/…/files/…, so compare
  // the file name only
  const fileKey = (src) => (src || '').split('?')[0].split('/').pop().replace(/_\d+x\d*(?=\.)/, '').toLowerCase();
  // Adds the photos that aren't in the strip yet, after the ones already there, so nothing already shown reloads
  // or jumps (the card's photos go in first, the rest of the product's follow)
  // raw: the card's own photos, kept at the exact URL the card loaded so they come from the cache (re-sizing them to
  // 480 re-downloaded them, and the first frames sat blank while the sheet slid up); a sharper file swaps in once
  // loaded when the card's is smaller than the frame. New photos fade in once loaded.
  // File width for a strip photo: its frame (desktop panel: 78% of the panel less padding, at most 324px; phones: 42%
  // of the sheet) times the screen's pixel density, in 100px steps (Van 2026-10-22: "image a bit blur"). Measured on
  // the frame's HEIGHT (4:5, so width × 1.25): square packshots fill a portrait frame by its height, so a file only
  // as wide as the frame was still stretched ~1.25×
  const wide = window.matchMedia('(min-width: 750px)');
  const photoWidth = () => {
    const frame = wide.matches ? (Math.min(480, window.innerWidth) - 64) * 0.78 : window.innerWidth * 0.42;
    return Math.min(1600, Math.ceil((frame * 1.25 * (window.devicePixelRatio || 1)) / 100) * 100);
  };
  const resized = (src, width) => {
    try {
      const url = new URL(src.startsWith('//') ? `https:${src}` : src, window.location.href);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (e) {
      return photoUrl(src, width);
    }
  };
  function paint (strip, urls, raw = false) {
    const shown = new Set([...strip.querySelectorAll('img')].map((img) => img.dataset.key));
    urls.forEach((src) => {
      const key = fileKey(src);
      if (!src || shown.has(key) || strip.children.length >= 8) return;
      shown.add(key);
      const frame = document.createElement('span');
      frame.className = 'sg-qa-sheet__photo';
      const img = document.createElement('img');
      img.dataset.key = key;
      img.alt = '';
      img.decoding = 'async';
      img.width = 384;
      img.height = 480;
      const done = () => frame.classList.add('is-loaded');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      const width = photoWidth();
      img.src = raw ? src : photoUrl(src, width);
      if (img.complete && img.naturalWidth) done();
      frame.append(img);
      strip.append(frame);
      // a card photo loaded smaller than its frame here (the first photo looked soft): the sharper file replaces it
      // once loaded, so nothing goes blank
      const loaded = Number(new URL(src.startsWith('//') ? `https:${src}` : src, window.location.href).searchParams.get('width')) || 0;
      if (raw && loaded && loaded < width) {
        const sharp = new Image();
        const sharpSrc = resized(src, width);
        sharp.addEventListener('load', () => { if (img.isConnected) img.src = sharpSrc; }, { once: true });
        sharp.src = sharpSrc;
      }
    });
  }
  const productPhotos = (handle) => {
    if (!photoCache.has(handle)) {
      photoCache.set(handle, fetch(`/products/${handle}.js`).then((r) => (r.ok ? r.json() : null)).then((p) => (p && p.images) || []).catch(() => []));
    }
    return photoCache.get(handle);
  };
  function gallery (popup, { link, images = [] }) {
    const content = popup.querySelector('.advanced-modal__content');
    if (!content) return;
    content.querySelector('.sg-qa-sheet__gallery-wrap')?.remove();
    content.querySelector('.sg-qa-sheet__gallery')?.remove();
    const strip = document.createElement('div');
    strip.className = 'sg-qa-sheet__gallery';
    // Prev / next for a mouse (Van 2026-10-22: "show big photo and swipe like mobile" in the desktop panel); phones
    // hide them and swipe (design-system.css)
    const wrap = document.createElement('div');
    wrap.className = 'sg-qa-sheet__gallery-wrap';
    const arrow = (dir) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `sg-qa-sheet__arrow sg-qa-sheet__arrow--${dir}`;
      button.setAttribute('aria-label', dir === 'prev' ? 'Previous photo' : 'Next photo');
      button.addEventListener('click', () => {
        const step = strip.firstElementChild ? strip.firstElementChild.getBoundingClientRect().width + 8 : strip.clientWidth;
        strip.scrollBy({ left: dir === 'prev' ? -step : step, behavior: 'smooth' });
      });
      return button;
    };
    const prev = arrow('prev');
    const next = arrow('next');
    const syncArrows = () => {
      prev.disabled = strip.scrollLeft <= 2;
      next.disabled = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 2;
      wrap.classList.toggle('is-single', strip.scrollWidth <= strip.clientWidth + 2);
    };
    strip.addEventListener('scroll', syncArrows, { passive: true });
    new MutationObserver(() => requestAnimationFrame(syncArrows)).observe(strip, { childList: true });
    wrap.append(strip, prev, next);
    // loaded card photos as they are; one the card never showed (e.g. the hover photo on phones) is sized like the rest
    images.forEach((img) => {
      if (img.complete && img.naturalWidth && img.currentSrc) paint(strip, [img.currentSrc], true);
      else paint(strip, [img.getAttribute('src') || img.currentSrc || '']);
    });
    content.prepend(wrap);
    requestAnimationFrame(syncArrows);
    const href = link?.getAttribute('href') || '';
    const handle = (href.match(/\/products\/([^/?#]+)/) || [])[1];
    if (!handle) return;
    productPhotos(handle).then((urls) => { if (urls.length > 1 && strip.isConnected) paint(strip, urls); });
  }

  window.sgQaOptions = { header, build, sync, gallery, photos: productPhotos };
})();
